import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Upload, X, CheckCircle, AlertCircle, FileImage, Film, Info } from 'lucide-react';

const VerificationSubmission = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [proofType, setProofType] = useState('sketch');
  const [notes, setNotes] = useState('');
  const [previews, setPreviews] = useState([]);

  const BACKEND_URL = '';  // Use relative URLs

  useEffect(() => {
    if (!user || (user.role !== 'artist' && user.role !== 'author')) {
      navigate('/');
      return;
    }

    loadVerificationStatus();
  }, [user, navigate]);

  const loadVerificationStatus = async () => {
    try {
      const response = await axios.get('/api/verification/status', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setVerificationStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load verification status:', error);
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validate file count
    if (selectedFiles.length + files.length > 10) {
      alert('Maximum 10 files allowed');
      return;
    }

    // Validate file sizes and types
    const validFiles = [];
    const newPreviews = [];

    files.forEach((file) => {
      // Check size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        alert(`${file.name} exceeds 50MB limit`);
        return;
      }

      // Check type
      const allowedTypes = [
        'image/jpeg', 'image/png', 'image/webp', 'image/gif',
        'video/mp4', 'video/quicktime', 'video/webm'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        alert(`${file.name} is not a supported file type`);
        return;
      }

      validFiles.push(file);

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push({
            file,
            url: reader.result,
            type: 'image'
          });
          setPreviews([...previews, ...newPreviews]);
        };
        reader.readAsDataURL(file);
      } else {
        newPreviews.push({
          file,
          url: null,
          type: 'video'
        });
        setPreviews([...previews, ...newPreviews]);
      }
    });

    setSelectedFiles([...selectedFiles, ...validFiles]);
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedFiles.length === 0) {
      alert('Please upload at least one proof file');
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('proof_files', file);
      });
      formData.append('proof_type', proofType);
      formData.append('notes', notes);

      await axios.post('/api/verification/submit', formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Verification request submitted successfully! We\'ll review it shortly.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Verification submission failed:', error);
      alert(error.response?.data?.detail || 'Failed to submit verification request');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  // Already verified
  if (verificationStatus?.verified) {
    return (
      <div className="min-h-screen bg-black text-white py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-4">You're Verified!</h1>
          <p className="text-zinc-400 text-lg mb-8">
            Your account has the Human Verified badge. This shows your audience that you're a real, authentic creator.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Pending review
  if (verificationStatus?.status === 'pending') {
    return (
      <div className="min-h-screen bg-black text-white py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-yellow-500 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-12 h-12 text-black" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Verification Pending</h1>
          <p className="text-zinc-400 text-lg mb-4">
            Your verification request is under review by our admin team.
          </p>
          <p className="text-zinc-500 mb-8">
            Submitted: {new Date(verificationStatus.submitted_at).toLocaleDateString()}
          </p>
          {verificationStatus.proof_files && verificationStatus.proof_files.length > 0 && (
            <div className="bg-zinc-900 rounded-xl p-6 mb-8">
              <h3 className="font-semibold mb-4">Submitted Files ({verificationStatus.proof_files.length})</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {verificationStatus.proof_files.map((file, index) => (
                  <div key={index} className="text-sm text-zinc-400">
                    {file.original_name}
                  </div>
                ))}
              </div>
            </div>
          )}
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-zinc-700 hover:bg-zinc-600 text-white font-semibold rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Rejected - allow resubmission
  if (verificationStatus?.status === 'rejected') {
    return (
      <div className="min-h-screen bg-black text-white py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-red-500 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
            <X className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-4">Verification Not Approved</h1>
          <p className="text-zinc-400 text-lg mb-4">
            Your previous verification request was not approved.
          </p>
          {verificationStatus.admin_notes && (
            <div className="bg-zinc-900 rounded-xl p-6 mb-8">
              <h3 className="font-semibold mb-2">Admin Notes:</h3>
              <p className="text-zinc-400">{verificationStatus.admin_notes}</p>
            </div>
          )}
          <p className="text-zinc-500 mb-8">
            You can submit a new verification request with better proof of your work.
          </p>
          <button
            onClick={() => {
              setVerificationStatus({ ...verificationStatus, status: 'none' });
            }}
            className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg"
          >
            Submit New Request
          </button>
        </div>
      </div>
    );
  }

  // Submission form
  return (
    <div className="min-h-screen bg-black text-white py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4">Get Human Verified</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Prove you're a real artist and earn the Human Verified badge. This helps your audience trust your work and distinguishes you from AI-generated content.
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6 mb-8">
          <div className="flex gap-3">
            <Info className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-400 mb-2">What You Need to Submit:</h3>
              <ul className="text-zinc-300 space-y-2">
                <li>• <strong>Sketches or WIP (Work in Progress)</strong>: Show your creative process</li>
                <li>• <strong>Process Videos</strong>: Timelapse or recording of you creating</li>
                <li>• <strong>Reference Photos</strong>: Photos of your workspace, tools, or materials</li>
                <li>• <strong>Multiple Angles</strong>: Different stages of your artwork</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-zinc-900 rounded-xl p-8">
          <div className="space-y-6">
            {/* Proof Type Selection */}
            <div>
              <label className="block text-sm font-semibold mb-3">What type of proof are you uploading?</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { value: 'sketch', label: 'Sketches', icon: FileImage },
                  { value: 'wip', label: 'WIP Shots', icon: FileImage },
                  { value: 'video', label: 'Process Video', icon: Film },
                  { value: 'reference', label: 'Reference Photos', icon: FileImage }
                ].map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setProofType(type.value)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      proofType === type.value
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <type.icon className="w-6 h-6 mx-auto mb-2" />
                    <div className="text-sm font-medium">{type.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-semibold mb-3">
                Upload Proof Files (Max 10 files, 50MB each)
              </label>
              <div className="border-2 border-dashed border-zinc-700 rounded-xl p-8 text-center hover:border-orange-500 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  className="sr-only"
                  id="verification-upload"
                />
                <label htmlFor="verification-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 mx-auto mb-4 text-zinc-500" />
                  <p className="text-zinc-400 mb-2">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-sm text-zinc-500">
                    Supports: JPEG, PNG, WebP, GIF, MP4, MOV, WebM
                  </p>
                </label>
              </div>
            </div>

            {/* File Previews */}
            {selectedFiles.length > 0 && (
              <div>
                <label className="block text-sm font-semibold mb-3">
                  Selected Files ({selectedFiles.length}/10)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {previews.map((preview, index) => (
                    <div key={index} className="relative group">
                      {preview.type === 'image' ? (
                        <img
                          src={preview.url}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-full h-32 bg-zinc-800 rounded-lg flex items-center justify-center">
                          <Film className="w-8 h-8 text-zinc-500" />
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <p className="text-xs text-zinc-400 mt-1 truncate">
                        {selectedFiles[index].name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-semibold mb-3">
                Additional Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Tell us about your creative process, tools you use, or any other relevant information..."
                rows="4"
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-zinc-700 hover:bg-zinc-600 text-white font-semibold rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || selectedFiles.length === 0}
                className="flex-1 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit for Verification'}
              </button>
            </div>
          </div>
        </form>

        {/* Benefits Section */}
        <div className="mt-12 bg-zinc-900 rounded-xl p-8">
          <h3 className="text-2xl font-bold mb-6 text-center">Benefits of Being Verified</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-blue-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-400" />
              </div>
              <h4 className="font-semibold mb-2">Trust Badge</h4>
              <p className="text-sm text-zinc-400">
                Display the Human Verified badge on your profile and all your content
              </p>
            </div>
            <div className="text-center">
              <div className="bg-green-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h4 className="font-semibold mb-2">Stand Out</h4>
              <p className="text-sm text-zinc-400">
                Differentiate yourself from AI-generated content and fake accounts
              </p>
            </div>
            <div className="text-center">
              <div className="bg-purple-500/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-purple-400" />
              </div>
              <h4 className="font-semibold mb-2">Priority Support</h4>
              <p className="text-sm text-zinc-400">
                Get faster responses and priority consideration for featured content
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationSubmission;
